import { ApolloClient, InMemoryCache } from "@apollo/client"; // HttpLink remove
import { relayStylePagination } from "@apollo/client/utilities";
import { createUploadLink } from "apollo-upload-client";
import { profileCompletionVar } from "graphql/variables";
import { EXTERNAL_LINK_VALUES } from "./externalLinkTypes";
import {
  QUESTION_SETTINGS,
  QUESTION_TYPE_CONFIGS,
  QUESTION_TYPE_OPTIONS,
  FILE_UPLOAD_TYPES,
  FILE_UPLOAD_LIMITS,
  FILE_UPLOAD_DEFAULTS,
  FILE_TYPE_OPTIONS,
  fileTypeOptions,
  fileDropzoneProps,
  renderAssessmentAnswer,
} from "./assessmentConfigs";

const APPLICATION_PREVIEW_TOKEN = () => {
  const pathname = window.location.pathname;

  let applicationPreviewToken = null;

  const match = pathname.match(/\/missions\/applications\/([^/]+)\/preview/);

  if (match) {
    const applicationId = match[1];
    applicationPreviewToken = localStorage.getItem(`application-preview-token-${applicationId}`);
  }

  return applicationPreviewToken;
};

const CSRF_TOKEN = document?.querySelector("meta[name=csrf-token]")?.getAttribute("content");

const CACHE = new InMemoryCache({
  typePolicies: {
    InterviewResponseAnswer: {
      keyFields: ["interviewResponseAnswerId"],
    },
    Query: {
      fields: {
        timeSheets: relayStylePagination(),
        partners: relayStylePagination(),
        profileCompletion: {
          read() {
            return profileCompletionVar();
          },
        },
      },
    },
  },
});

const CLIENT = new ApolloClient({
  link: new createUploadLink({
    // Replaced HttpLink with createUploadLink to allow for file uploads
    credentials: "include",
    headers: {
      "X-CSRF-Token": CSRF_TOKEN,
      "access-token": localStorage.getItem("token"),
      client: localStorage.getItem("client"),
      uid: localStorage.getItem("uid"),
      "application-preview-token": APPLICATION_PREVIEW_TOKEN(),
    },
  }),
  cache: CACHE,
  connectToDevTools: true, // TODO: Add an environment variable to put this to false for production
});

const LOCALE = "en";
const GOOGLE_API_KEY = process.env.GOOGLE_API_KEY;
const AM5_LICENSE_KEY = process.env.AM5_LICENSE_KEY;
const LINKED_IN_PREFIX = `https://www.linkedin.com/in/`;
const CLOUDFRONT_URL = "https://d3e4xd8b7ld2vz.cloudfront.net/";
const CODE_CHALLENGE_PASS_THRESHOLD = 60;
const MISSIONS_RESULTS_PER_PAGE = 20;
const SQUAD_INSIGHTS_PER_PAGE = 20;
const CLIENT_MEETING_URL = "https://meetings.hubspot.com/ashley224?embed=true";
const ORGANIZATIONS_RESULTS_PER_PAGE = 20;
const MISSION_ROLE_APPLICANTS_PER_PAGE = 100;
const USER_ACCESS_RESULTS_PER_PAGE = 40;
const CODE_CHALLENGES_RESULTS_PER_PAGE = 50;
const TIME_CARD_RESULTS_PER_PAGE = 50;
const TAG_RESULTS_PER_PAGE = 200;
const MATCHING_EMAIL = "matching@mission.dev";
const MAX_API_RECORDS = 10000; // Maximum records to fetch for pagination
const MARKETING_WEBSITE_URL = "https://mission.dev";
const OTP_LENGTH = 6;

export { default as TIME_PERIODS } from "./timePeriods";
export { default as COUNTRIES } from "./countries";
export { default as NO_OF_SOFTWARE_ENGINEERS } from "./noOfSoftwareEngineers";
export { default as NO_OF_EMPLOYEES } from "./noOfEmployees";
export { default as CURRENCIES, CURRENCIES_WITH_FLAGS } from "./currencies";
export { CURRENCY_NAMES } from "./currencies";
export { default as CHALLENGES, CHALLENGE_ITEM_TYPES, CHALLENGE_ITEM_ORDER } from "./challenges";
export { default as EXTERNAL_LINK_TYPES } from "./externalLinkTypes";
export { default as PAYMENT_FREQUENCIES } from "./paymentFrequencies";
export { default as PROFILE_PREFERENCES } from "./profilePreferences";
export { default as BANNER_TYPE } from "./bannerTypes";
export { default as CAPACITY_STATUS } from "./capacityStatus";
export { default as PARTNER_CHANNELS } from "./partnerChannels";
export { default as DISCIPLINES } from "./disciplines";
export { default as ARCHETYPES_SYMBOLS } from "./archetypeSymbols";
export {
  default as ROLES,
  ROLE_STATES_OPTIONS,
  ROLE_STATES_LABELS,
  ROLE_ATS_REPORT_MODES,
  ROLES_TEMPLATE_VARIABLES,
} from "./roles";
export { default as MEMBER_STATUS } from "./memberStatus";
export { default as REJECTED_REASON } from "./rejectedReason";
export { default as APPLICANT_TYPES, NOTIFIABLE_APPLICANT_TYPES } from "./applicantTypes";
export { default as AVAILABILITIES } from "./availabilities";
export { default as YEARS_OF_EXPERIENCE } from "./yearsOfExperience";
export { default as CLOUDFRONT } from "./availabilities";
export { default as TIME_CARD_STATUS, TIME_CARD_OPTIONS } from "./timeCardStatus";
export { default as DATE_FORMAT_TYPE } from "./dateFormatType";
export {
  default as TIMELINE_EVENTS,
  TIMELINE_EVENT_TYPES,
  formatTimelineResourceData,
  getTimelineTitleKey,
} from "./timelineEvents";
export { default as ACTIVITY_TYPES } from "./activityTypes";
export { default as ARCHIVE_REASON } from "./archiveReason";
export { default as TEAM_CATEGORY_SCORES } from "./teamCategoryScores";
export { default as REQUIREMENT_TYPES } from "./requirementTypes";
export { default as CHALLENGES_TYPES, DEFAULT_CHALLENGE } from "./challengesTypes";
export { default as CHEATING_TYPES } from "./cheatingTypes";
export {
  default as TERM_SHEET_TYPES,
  TERM_SHEET_PRICING_TYPES,
  TERM_SHEET_INVERSE_PRICING_TYPES,
} from "./termSheetTypes";
export { default as SETTINGS_SIDENAV } from "./settings";
export { default as CODEHOSTINGSERVICES } from "./codeHostingServices";
export { default as COMMENTABLE_TYPES } from "./commentableTypes";
export { default as DIRECTION, MOVE_DIRECTION } from "./direction";
export { default as CODE_CHALLENGE_TYPES } from "./codeChallengeTypes";
export { default as PAGES } from "./pages";
export { default as ERROR_CODES } from "./errorCodes";
export { default as ARCHETYPES, ARCHETYPES_OPTIONS, TALENT_SIMPLE_ARCHETYPES_OPTIONS } from "./archetypes";
export { default as RESOURCE_TYPES, POLYMORPHIC_RESOURCE_TYPES } from "./resourceTypes";
export { default as CODE_CHALLENGE_STATUS } from "./codeChallengeStatus";
export { default as DEEL_TASK_STATUS } from "./deelTaskStatus";
export { default as ORIGINS } from "./origins";
export { default as TEAM_ACTIVITY_TYPES } from "./teamActivityTypes";
export { default as ORGANIZATION_SIZE_OPTIONS } from "./organizationSizeOptions";
export { default as PARTNER_TYPES } from "./partnerTypes";
export { default as LIST_TYPES, LIST_TYPES_HUMANIZED } from "./listTypes";
export { default as USER_FORM_CONFIGS, USER_NAME_MAX_LENGTH } from "./userFormConfigs";
export { default as ENGINEERING_LEVELS } from "./engineeringLevels";
export { default as SQUAD_STATUS } from "./squadStatus";
export { default as WORK_ARRANGEMENT_OPTIONS, WORK_ARRANGEMENTS } from "./workArrangements";
export { default as ENGAGEMENT_TYPES_OPTIONS, ENGAGEMENT_TYPES } from "./engagementTypes";
export {
  GLOBAL_VALUE_TYPES,
  OPERATORS,
  USER_TYPES_WITH_FILTER_ACCESS,
  FLAG_FILTER_OPTIONS,
  GEO_LOCATION_FILTER_DEFAULTS,
  canUseFilter,
  getTransformedFilters,
  getAllowedSortOptions,
} from "./globalFormFilters";
export { default as APPLICANT_LIST_FILTERS_CONFIG, STICKY_FILTER_KEYS_FOR_CLIENTS } from "./applicantListFilters";
export { default as TERMSHEET_EVENTS } from "./termSheetEvents";
export { default as USER_TYPES, USER_TRANSITIONS, USER_TRANSITION_DEFAULTS } from "./userTypes";
export { default as USER_ACTIVATION_STATUSES } from "./userActivationStatus";
export { default as FEATURE_ENTITLEMENTS } from "./featureEntitlements";
export { default as READ_ONLY_CHANNELS } from "./readOnlyChannels";
export { default as CHALLENGE_CATEGORIES } from "./challengeCategories";
export { default as BILLING_ACCOUNT_INTERVALS } from "./billingAccountIntervals";
export { default as INTERVIEW_STATUSES } from "./interviewStatuses";
export { default as TERM_SHEET_SCENARIOS } from "./termSheetScenarios";
export { default as CHANGE_KEYS } from "./termSheetScenarioChangeKeys";
export { default as SENTIMENTS } from "./sentimentTypes";
export { default as INSIGHT_TYPES, INSIGHT_TYPES_OPTIONS } from "./insightTypes";
export { default as INSIGHT_METRICS_OPTIONS } from "./insightMetrics";
export { default as FEEDBACK_OPTIONS } from "./feedbackOptions";
export { default as SQUAD_METRIC_TYPES } from "./squadMetricTypes";
export { default as TAB_OPTIONS } from "./tabOptions";
export { default as RATE_FREQUENCIES, CLIENT_BUDGET_RATE_FREQUENCIES, MINIMUM_TARGET_RATE } from "./rateFrequencies";
export { default as PROFILE_STATUS_MAPPINGS } from "./profileStatusMappings";
export {
  default as SORT,
  APPLICANT_LIST_SORTING_OPTIONS,
  NETWORK_PAGE_KEYWORD_SEARCH_SORT,
  APPLICANT_LIST_KEYWORD_SEARCH_SORT,
} from "./sorts";
export { default as CONNECTION_STATES, CONNECTION_TYPES } from "./connectionStates";
export { default as TEAM_TYPES_OPTIONS, TEAM_TYPES } from "./teamTypes";
export { default as VETTING_STEPS_OPTIONS, VETTING_STEPS } from "./vettingSteps";
export { default as DAYS_OF_WEEK } from "./daysOfWeek";
export { default as ASSESSMENT_CONFIGS } from "./assessmentConfigs";
export { default as APPLICANT_LIST_COLUMNS, calculateApplicationCompletionPercentage } from "./applicantListColumns";
export { default as MISSION_ROLE_STATES, isDraftInProgress } from "./missionRoleStates";
export { default as APPLICATION_STEPS } from "./applicationSteps";
export { getApplicationQuestionnaireItems } from "./legacyApplicationQuestions";
export { default as MISSION_ROLE_ELIGIBILITY } from "./missionRoleEligibility";
export { default as MISSION_ROLES_COLUMNS, STATUS_COLORS } from "./missionRolesColumns";
export { default as TALENT_SIMPLE_ROLE_SECTIONS, TALENT_SIMPLE_ROLES_CONFIG } from "./talentSimpleRoleSections";
export {
  default as CUSTOMER_PORTAL_VERSIONS,
  CUSTOMER_PORTAL_VERSION_OPTIONS,
  CUSTOMER_PORTAL_VERSION_BRANDS,
  isTalentSimplePortal,
  isMissionDevPortal,
} from "./customerPortalVersions";
export {
  default as BRANDS,
  BRAND_LOGO_VARIANTS,
  BRAND_CONFIG,
  getCurrentBrand,
  isTalentSimpleBrand,
  getBrandConfig,
  getBrandRichTextElements,
} from "./brands";
export { default as LEGAL_DOCUMENT_TYPES } from "./legalDocuments";
export { default as RATINGS, RATING_CONTENT_TYPES, RATING_RESOURCE_TYPES } from "./ratings";
export { default as RESOURCE_LINKS_MAPPING } from "./omnisearchResources";
export { FRONTEND_EVENT_KEYS, FRONTEND_EVENTABLE_TYPES } from "./frontendEvents";
export { default as MISSION_ROLE_FORM_FIELDS, getLeftColFields, getRightColFields } from "./missionRoleFormFields";
export {
  default as ROLE_BUILDER_STEPS,
  BLUEPRINT_CARD_CHECKS,
  BLUEPRINT_TAB,
  CLEARED_JOB_DESCRIPTION_UPLOAD_VALUES,
  JOB_DESCRIPTION_TAB,
  ROLE_BUILDER_CONFIG,
  ROLE_BUILDER_STEP,
  getBlueprintVettingRows,
  getRoleBuilderStep,
  getRoleBuilderStepIndex,
  getRoleBuilderSteps,
  getRoleBuilderSummary,
} from "./talentSimpleRoleBuilder";
export {
  BLUEPRINTS_CONFIG,
  BLUEPRINT_GENERATION_CONFIG,
  MISSION_ROLE_TEMPLATE_STATE,
  getBlueprintFormValues,
  isBlueprintPublished,
  isBlueprintGenerating,
  isBlueprintGenerationCompleted,
  isBlueprintGenerationFailed,
  hasBlueprintGenerationEnded,
  isBlueprintAgentTask,
} from "./blueprints";
export { default as DOMAIN_NAMES } from "./domains";
export {
  getAwaitingTaskFields,
  getBlueprintFields,
  getInformationFields,
  getJobDescriptionReviewFields,
  getJobDescriptionUploadFields,
} from "./roleBuilderFields";
export { default as INTERVIEW_TYPES, INTERVIEW_TYPES_HUMANIZED } from "./interviewTypes";
export {
  default as AI_ROLE_BUILDER_STATUS,
  AI_ROLE_BUILDER_CONFIG,
  AI_ROLE_BUILDER_TASKS_CONFIG,
  AI_ROLE_BUILDER_TASK_DISPLAY_STATE,
  AI_ROLE_BUILDER_TASK_STATUS,
  AI_ROLE_INTERVIEW_QUESTIONS_CONFIG,
  AI_ROLE_BUILDER_STEP,
  getRoleBuilderTaskDisplayState,
  isProcessingStatus,
  isTaskCompleted,
  isTaskDiscarded,
  isTaskFailed,
  isTaskPolling,
} from "./aiRoleBuilderStatuses";
export {
  default as INTERVIEW_QUESTION_PHASE,
  QUESTION_PRE_TIMER_SECONDS,
  QUESTION_COUNTDOWN_SECONDS,
  RECORDING_TIME_SECONDS,
  INTERVIEW_PHASE,
  INTERVIEW_MEDIA_DEVICES,
  INTERVIEW_MEDIA_DEVICES_STATUS,
  INTERVIEW_QUESTION_TIME_LIMIT_OPTIONS,
  MIN_RECORDING_SIZE_BYTES,
} from "./interviewQuestions";
export {
  default as TIMELINE_LIST_FILTERS_CONFIG,
  TIMELINE_LIST_SORTING_OPTIONS,
  TIMELINE_LIST_SORTING_VALUES,
} from "./timelineListFilters";
export {
  default as NETWORK_LIST_FILTERS_CONFIG,
  STICKY_NETWORK_FILTER_KEYS,
  NETWORK_LIST_SORTING_OPTIONS,
  NETWORK_LIST_SORTING_VALUES,
  NETWORK_LIST_BADGES_OPTIONS,
  NETWORK_LIST_STATUS_OPTIONS,
} from "./networkListFilters";
export { default as DAILY_REPORT_STATUS, DAILY_REPORT_QUESTION_CATEGORIES } from "./dailyReportStatus";
export {
  default as LEGACY_TERMSHEET_COLUMNS_CONFIG,
  TERMSHEET_STATUS_COLORS,
  TERM_SHEET_STATUSES,
  TERM_SHEET_STATES,
  getStateText,
  isActiveTermSheet,
  isDraftTermSheet,
  isInactiveTermSheet,
  calculateTotals,
  TOTALS_SUM_COLUMNS,
  shouldShowTotals,
  getTotalsDisplayConfig,
  getTransformedParams,
  normalizeValue,
  hasValueChanged,
} from "./legacyTermSheet";
export {
  default as AI_VIDEO_INTERVIEW_STATUS,
  AI_VIDEO_INTERVIEW_CONFIG,
  AI_VIDEO_MODAL_MODE,
  AI_VIDEO_TASK_DISPLAY_STATE,
  isVideoTaskPending,
  isVideoTaskCompleted,
  isVideoTaskFailed,
  isVideoTaskConfirmed,
  isVideoTaskPollingState,
  isVideoTaskErrored,
  isVideoTaskFinalizing,
  isVideoTaskReviewable,
  resolveVideoTask,
  getPreferredGeneratedVideoUrl,
  getVideoTaskDisplayState,
} from "./aiVideoInterviewStatuses";
export { VIDEO_INTERVIEW_CONTENT_PREVIEW_LENGTH } from "./videoInterviews";
export { default as TAG_CATEGORIES } from "./tags";
export { default as AGENT_TASK_STATE, PROFILE_SECTIONS } from "./agentTask";
export {
  default as EVENT_DURATION_OPTIONS,
  DEFAULT_AVAILABILITY,
  DEFAULT_DURATION_MINUTES,
  EVENT_NAME_MAX_LENGTH,
  INTERVIEW_EVENT_STATUS,
  INTERVIEW_EVENT_STATUS_MESSAGES,
  isValidInterviewAvailability,
} from "./calendly";
export * from "./interviewLocations";
export * from "./interviewSchedulingRequests";

export {
  CLIENT,
  CACHE,
  LOCALE,
  CLOUDFRONT_URL,
  CSRF_TOKEN,
  GOOGLE_API_KEY,
  AM5_LICENSE_KEY,
  LINKED_IN_PREFIX,
  CODE_CHALLENGE_PASS_THRESHOLD,
  MISSIONS_RESULTS_PER_PAGE,
  SQUAD_INSIGHTS_PER_PAGE,
  CLIENT_MEETING_URL,
  ORGANIZATIONS_RESULTS_PER_PAGE,
  MISSION_ROLE_APPLICANTS_PER_PAGE,
  USER_ACCESS_RESULTS_PER_PAGE,
  CODE_CHALLENGES_RESULTS_PER_PAGE,
  TIME_CARD_RESULTS_PER_PAGE,
  TAG_RESULTS_PER_PAGE,
  EXTERNAL_LINK_VALUES,
  QUESTION_SETTINGS,
  QUESTION_TYPE_CONFIGS,
  QUESTION_TYPE_OPTIONS,
  FILE_UPLOAD_TYPES,
  FILE_UPLOAD_LIMITS,
  FILE_UPLOAD_DEFAULTS,
  FILE_TYPE_OPTIONS,
  fileTypeOptions,
  fileDropzoneProps,
  renderAssessmentAnswer,
  MATCHING_EMAIL,
  MAX_API_RECORDS,
  MARKETING_WEBSITE_URL,
  OTP_LENGTH,
};
